<script>
import { ref } from 'vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import VideoCallButton from '../VideoCallButton.vue';
import RequestPaymentButton from '../RequestPaymentButton.vue';
import SendFlightOptionsButton from '../SendFlightOptionsButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import { OnClickOutside } from '@vueuse/components';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ComposerToolsMenu from './ComposerToolsMenu.vue';

// Tools the agent has pinned to the toolbar. Everything else stays one click
// away inside the "More tools" menu.
const PINNED_TOOLS_UI_SETTINGS_KEY = 'composer_pinned_tools';
const DEFAULT_PINNED_TOOLS = ['emoji', 'attach'];

export default {
  name: 'ReplyBottomPanel',
  components: {
    NextButton,
    FileUpload,
    VideoCallButton,
    RequestPaymentButton,
    SendFlightOptionsButton,
    ComposerToolsMenu,
    OnClickOutside,
  },
  mixins: [inboxMixin],
  props: {
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'toggleQuotedReply',
  ],
  setup(props) {
    const {
      uiSettings,
      updateUISettings,
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
    } = useUISettings();

    const uploadRef = ref(false);

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      uiSettings,
      updateUISettings,
      setSignatureFlagForInbox,
      fetchSignatureFlagFromUISettings,
      uploadRef,
    };
  },
  data() {
    return {
      isToolsMenuOpen: false,
      // Measured by a ResizeObserver; in the compact panel the pinned tool
      // icons collapse into the "More tools" menu so the row never overflows.
      panelWidth: null,
      // Integration buttons decide their own availability, so they report it
      // back for the "More tools" menu to list them.
      integrationAvailability: {
        video_call: false,
        request_payment: false,
        send_flight_options: false,
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    showMessageSignatureButton() {
      if (this.isEditorDisabled) return false;
      return !this.isOnPrivateNote;
    },
    sendWithSignature() {
      // channelType is sourced from inboxMixin
      return this.fetchSignatureFlagFromUISettings(this.channelType);
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    showVideoCallButton() {
      return (
        (this.isAWebWidgetInbox || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.isEditorDisabled
      );
    },
    showCustomerActionButtons() {
      return !this.isOnPrivateNote && !this.isEditorDisabled;
    },
    pinnedTools() {
      const pinned = this.uiSettings[PINNED_TOOLS_UI_SETTINGS_KEY];
      return Array.isArray(pinned) ? pinned : DEFAULT_PINNED_TOOLS;
    },
    isCompact() {
      return this.panelWidth !== null && this.panelWidth < 440;
    },
    availableTools() {
      return [
        {
          id: 'emoji',
          icon: 'i-ph-smiley-sticker',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.EMOJI'),
          isAvailable: !this.isEditorDisabled,
        },
        {
          id: 'attach',
          icon: 'i-ph-paperclip',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.ATTACH'),
          isAvailable: this.showAttachButton,
        },
        {
          id: 'audio_recorder',
          icon: 'i-ph-microphone',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.AUDIO_RECORDER'),
          isAvailable: this.showAudioRecorderButton,
        },
        {
          id: 'signature',
          icon: 'i-ph-signature',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.SIGNATURE'),
          isAvailable: this.showMessageSignatureButton,
        },
        {
          id: 'quoted_reply',
          icon: 'i-ph-quotes',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.QUOTED_REPLY'),
          isAvailable: this.showQuotedReplyToggle,
        },
        {
          id: 'whatsapp_templates',
          icon: 'i-ph-whatsapp-logo',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.WHATSAPP_TEMPLATES'),
          isAvailable: this.enableWhatsAppTemplates,
        },
        {
          id: 'content_templates',
          icon: 'i-ph-whatsapp-logo',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.CONTENT_TEMPLATES'),
          isAvailable: this.enableContentTemplates,
        },
        {
          id: 'insert_article',
          icon: 'i-ph-article-ny-times',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.INSERT_ARTICLE'),
          isAvailable: !!this.enableInsertArticleInReply,
        },
        {
          id: 'video_call',
          icon: 'i-ph-video-camera',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.VIDEO_CALL'),
          isAvailable:
            this.showVideoCallButton && this.integrationAvailability.video_call,
        },
        {
          id: 'request_payment',
          icon: 'i-ph-receipt',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.REQUEST_PAYMENT'),
          isAvailable:
            this.showCustomerActionButtons &&
            this.integrationAvailability.request_payment,
        },
        {
          id: 'send_flight_options',
          icon: 'i-ph-airplane-tilt',
          label: this.$t('CONVERSATION.REPLYBOX.TOOLS.SEND_FLIGHT_OPTIONS'),
          isAvailable:
            this.showCustomerActionButtons &&
            this.integrationAvailability.send_flight_options,
        },
      ]
        .filter(tool => tool.isAvailable)
        .map(tool => ({ ...tool, isPinned: this.isToolPinned(tool.id) }));
    },
  },
  mounted() {
    ActiveStorage.start();
    this.panelResizeObserver = new ResizeObserver(([entry]) => {
      this.panelWidth = entry.contentRect.width;
    });
    this.panelResizeObserver.observe(this.$el);
  },
  beforeUnmount() {
    this.panelResizeObserver?.disconnect();
  },
  methods: {
    toggleMessageSignature() {
      this.setSignatureFlagForInbox(this.channelType, !this.sendWithSignature);
    },
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
    isToolPinned(id) {
      return this.pinnedTools.includes(id);
    },
    // Template-only guard: pinned icons are hidden in the compact panel, where every
    // tool stays reachable through the "More tools" menu. Kept separate from
    // isToolPinned so collapsing never changes the stored pin list.
    showsPinnedTool(id) {
      return !this.isCompact && this.isToolPinned(id);
    },
    setIntegrationAvailability(id, isAvailable) {
      this.integrationAvailability[id] = isAvailable;
    },
    toggleToolPin(id) {
      const pinned = this.isToolPinned(id)
        ? this.pinnedTools.filter(toolId => toolId !== id)
        : [...this.pinnedTools, id];
      this.updateUISettings({ [PINNED_TOOLS_UI_SETTINGS_KEY]: pinned });
    },
    triggerTool(id) {
      this.isToolsMenuOpen = false;
      const actions = {
        emoji: () => this.toggleEmojiPicker(),
        // The file input stays mounted even when the tool is unpinned, so the
        // picker can be opened from here just like the keyboard shortcut does.
        attach: () => document.querySelector('#conversationAttachment').click(),
        audio_recorder: () => this.toggleAudioRecorder(),
        signature: () => this.toggleMessageSignature(),
        quoted_reply: () => this.$emit('toggleQuotedReply'),
        whatsapp_templates: () => this.$emit('selectWhatsappTemplate'),
        content_templates: () => this.$emit('selectContentTemplate'),
        insert_article: () => this.toggleInsertArticle(),
        video_call: () => this.$refs.videoCallButton.trigger(),
        request_payment: () => this.$refs.requestPaymentButton.trigger(),
        send_flight_options: () => this.$refs.sendFlightOptionsButton.trigger(),
      };
      actions[id]();
    },
  },
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-2 px-3 py-1.5"
    :class="wrapClass"
  >
    <div class="left-wrap">
      <slot name="leading" />
      <NextButton
        v-if="!isEditorDisabled && showsPinnedTool('emoji')"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        icon="i-ph-smiley-sticker"
        slate
        faded
        sm
        @click="toggleEmojiPicker"
      />
      <FileUpload
        v-if="showAttachButton"
        v-show="showsPinnedTool('attach')"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton && showsPinnedTool('audio_recorder')"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :icon="!isRecordingAudio ? 'i-ph-microphone' : 'i-ph-microphone-slash'"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <NextButton
        v-if="showMessageSignatureButton && showsPinnedTool('signature')"
        v-tooltip.top-end="signatureToggleTooltip"
        icon="i-ph-signature"
        slate
        faded
        sm
        @click="toggleMessageSignature"
      />
      <NextButton
        v-if="showQuotedReplyToggle && showsPinnedTool('quoted_reply')"
        v-tooltip.top-end="quotedReplyToggleTooltip"
        icon="i-ph-quotes"
        :variant="quotedReplyEnabled ? 'solid' : 'faded'"
        color="slate"
        sm
        :aria-pressed="quotedReplyEnabled"
        @click="$emit('toggleQuotedReply')"
      />
      <NextButton
        v-if="enableWhatsAppTemplates && showsPinnedTool('whatsapp_templates')"
        v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectWhatsappTemplate')"
      />
      <NextButton
        v-if="enableContentTemplates && showsPinnedTool('content_templates')"
        v-tooltip.top-end="'Content Templates'"
        icon="i-ph-whatsapp-logo"
        slate
        faded
        sm
        @click="$emit('selectContentTemplate')"
      />
      <VideoCallButton
        v-if="showVideoCallButton"
        ref="videoCallButton"
        :conversation-id="conversationId"
        :pinned="showsPinnedTool('video_call')"
        @availability="setIntegrationAvailability('video_call', $event)"
      />
      <RequestPaymentButton
        v-if="showCustomerActionButtons"
        ref="requestPaymentButton"
        :conversation-id="conversationId"
        :pinned="showsPinnedTool('request_payment')"
        @availability="setIntegrationAvailability('request_payment', $event)"
      />
      <!-- Beside Request Payment because it is the same kind of action: it composes
           something the customer receives. Capturing a brief is not, and lives in the
           Trips panel instead. -->
      <SendFlightOptionsButton
        v-if="showCustomerActionButtons"
        ref="sendFlightOptionsButton"
        :conversation-id="conversationId"
        :pinned="showsPinnedTool('send_flight_options')"
        @availability="
          setIntegrationAvailability('send_flight_options', $event)
        "
      />
      <transition name="modal-fade">
        <div
          v-show="uploadRef && uploadRef.dropActive"
          class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
        >
          <fluent-icon icon="cloud-backup" size="40" />
          <h4 class="text-2xl break-words text-n-slate-12">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
      <NextButton
        v-if="enableInsertArticleInReply && showsPinnedTool('insert_article')"
        v-tooltip.top-end="$t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')"
        icon="i-ph-article-ny-times"
        slate
        faded
        sm
        @click="toggleInsertArticle"
      />
      <OnClickOutside
        v-if="availableTools.length"
        class="relative flex"
        @trigger="isToolsMenuOpen = false"
      >
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TOOLS.MORE')"
          data-testid="composer-more-tools"
          icon="i-ph-dots-three"
          slate
          faded
          sm
          aria-haspopup="menu"
          :aria-expanded="isToolsMenuOpen"
          :aria-label="$t('CONVERSATION.REPLYBOX.TOOLS.MORE')"
          @click="isToolsMenuOpen = !isToolsMenuOpen"
        />
        <ComposerToolsMenu
          v-if="isToolsMenuOpen"
          :tools="availableTools"
          @trigger="triggerTool"
          @toggle-pin="toggleToolPin"
        />
      </OnClickOutside>
    </div>
    <div class="right-wrap">
      <slot name="trailing" />
      <NextButton
        :label="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.left-wrap {
  @apply items-center flex gap-2 flex-1 min-w-0;
}

.right-wrap {
  @apply flex items-center gap-2 flex-shrink-0;
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button {
    @apply enabled:bg-n-slate-9/20;
  }
}
</style>
